import { t } from "@excalidraw/excalidraw/i18n";
import { useCallback, useEffect, useState } from "react";

import { atom, useAtom } from "../app-jotai";
import { libraryApi, type ManagedUser } from "../data/libraryApi";

import { useAuth } from "./AuthContext";

import "./auth.scss";

export const usersDialogOpenAtom = atom(false);

const errorMessage = (err: unknown): string => {
  const msg = err instanceof Error ? err.message : String(err);
  if (msg.includes("409")) {
    return t("auth.usernameTaken");
  }
  if (msg.includes("400") && msg.toLowerCase().includes("password")) {
    return t("auth.passwordTooShort");
  }
  if (msg.includes("400") && msg.toLowerCase().includes("admin")) {
    return t("auth.cannotRemoveLastAdmin");
  }
  return msg;
};

export const UserManagementDialog = () => {
  const [open, setOpen] = useAtom(usersDialogOpenAtom);
  const { user } = useAuth();
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // new-user form
  const [newName, setNewName] = useState("");
  const [newPass, setNewPass] = useState("");
  const [newRole, setNewRole] = useState<"user" | "admin">("user");

  // inline password reset
  const [resetFor, setResetFor] = useState<string | null>(null);
  const [resetPass, setResetPass] = useState("");

  const reload = useCallback(() => {
    libraryApi
      .listUsers()
      .then(setUsers)
      .catch((e) => setError(errorMessage(e)));
  }, []);

  useEffect(() => {
    if (open) {
      setError(null);
      reload();
    }
  }, [open, reload]);

  if (!open) {
    return null;
  }

  const close = () => {
    setOpen(false);
    setNewName("");
    setNewPass("");
    setNewRole("user");
    setResetFor(null);
    setResetPass("");
    setError(null);
  };

  const addUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!newName.trim() || newPass.length < 6) {
      setError(t("auth.passwordTooShort"));
      return;
    }
    setBusy(true);
    try {
      await libraryApi.createUser({
        username: newName.trim(),
        password: newPass,
        role: newRole,
      });
      setNewName("");
      setNewPass("");
      setNewRole("user");
      reload();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const deleteUser = async (id: string) => {
    setError(null);
    try {
      await libraryApi.deleteUser(id);
      reload();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const submitReset = async (id: string) => {
    if (resetPass.length < 6) {
      setError(t("auth.passwordTooShort"));
      return;
    }
    setError(null);
    try {
      await libraryApi.updateUser(id, { password: resetPass });
      setResetFor(null);
      setResetPass("");
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <div
      className="pw-users"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          close();
        }
      }}
    >
      <div className="pw-users__dialog">
        <div className="pw-users__header">
          <h2>{t("auth.users")}</h2>
          <button type="button" aria-label={t("auth.close")} onClick={close}>
            ×
          </button>
        </div>

        <div className="pw-users__list">
          {users.map((u) => (
            <div key={u.id} className="pw-users__row">
              <span className="pw-users__row-name">
                {u.username}
                <span className="pw-users__row-role">
                  {u.role === "admin"
                    ? t("auth.roleAdmin")
                    : t("auth.roleUser")}
                </span>
              </span>
              {resetFor === u.id ? (
                <div className="pw-users__row-actions">
                  <input
                    type="password"
                    autoFocus
                    placeholder={t("auth.newPassword")}
                    value={resetPass}
                    onChange={(e) => setResetPass(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        void submitReset(u.id);
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="pw-users__btn"
                    onClick={() => void submitReset(u.id)}
                  >
                    {t("auth.save")}
                  </button>
                </div>
              ) : (
                <div className="pw-users__row-actions">
                  <button
                    type="button"
                    className="pw-users__btn"
                    onClick={() => {
                      setResetFor(u.id);
                      setResetPass("");
                    }}
                  >
                    {t("auth.resetPassword")}
                  </button>
                  <button
                    type="button"
                    className="pw-users__btn pw-users__btn--danger"
                    disabled={u.id === user.id}
                    title={u.id === user.id ? t("auth.cannotDeleteSelf") : ""}
                    onClick={() => void deleteUser(u.id)}
                  >
                    {t("auth.deleteUser")}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        <form className="pw-users__add" onSubmit={addUser}>
          <h3>{t("auth.addUser")}</h3>
          <input
            placeholder={t("auth.username")}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <input
            type="password"
            placeholder={t("auth.password")}
            value={newPass}
            onChange={(e) => setNewPass(e.target.value)}
          />
          <select
            value={newRole}
            onChange={(e) => setNewRole(e.target.value as "user" | "admin")}
          >
            <option value="user">{t("auth.roleUser")}</option>
            <option value="admin">{t("auth.roleAdmin")}</option>
          </select>
          {error && <div className="pw-users__error">{error}</div>}
          <button className="pw-users__submit" type="submit" disabled={busy}>
            {busy ? "…" : t("auth.addUser")}
          </button>
        </form>
      </div>
    </div>
  );
};
